import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  Sheet,
  Select,
  Option,
  Input,
  Chip,
  CircularProgress,
  Alert,
  Checkbox,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Stack,
  IconButton,
} from '@mui/joy';
import {
  Plus as Add,
  CheckCircle,
  Warning,
  ArrowsLeftRight as CompareArrows,
  ArrowLeft as ArrowBack,
  Trash as Delete,
  ArrowCounterClockwise as Undo,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../../lib/apiAccounting';
import type {
  BankReconciliation as BankReconciliationType,
  BankAccount,
  BankTransaction,
} from '../../../types/accounting';

// ─── Reconciliation Working Screen (QBO-style) ─────────────────────────
interface ReconcileWorkingProps {
  reconciliation: BankReconciliationType;
  onBack: () => void;
  onComplete: () => void;
}

const ReconcileWorking: React.FC<ReconcileWorkingProps> = ({ reconciliation: initialRecon, onBack, onComplete }) => {
  const [recon, setRecon] = useState<BankReconciliationType>(initialRecon);
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState<number | null>(null);
  const [confirmFinishOpen, setConfirmFinishOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean; message: string; color: 'success' | 'danger' | 'warning';
  }>({ open: false, message: '', color: 'success' });

  const showSnackbar = (message: string, color: 'success' | 'danger' | 'warning') => {
    setSnackbar({ open: true, message, color });
  };

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const txns = await apiAccountingClient.getReconciliationTransactions(recon.id);
      setTransactions(txns);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      showSnackbar('Failed to load transactions.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [recon.id]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleToggleClear = async (txn: BankTransaction) => {
    setClearing(txn.id);
    try {
      let updatedRecon: BankReconciliationType;
      if (txn.is_cleared) {
        updatedRecon = await apiAccountingClient.unclearTransaction(recon.id, txn.id);
      } else {
        updatedRecon = await apiAccountingClient.clearTransaction(recon.id, txn.id);
      }
      setRecon(updatedRecon);
      // Update local transaction state
      setTransactions(prev =>
        prev.map(t => t.id === txn.id ? { ...t, is_cleared: !t.is_cleared } : t)
      );
    } catch (err) {
      console.error('Failed to toggle transaction:', err);
      showSnackbar('Failed to update transaction.', 'danger');
    } finally {
      setClearing(null);
    }
  };

  const handleFinish = async (force: boolean) => {
    try {
      await apiAccountingClient.completeBankReconciliation(recon.id, force);
      setConfirmFinishOpen(false);
      showSnackbar('Reconciliation completed!', 'success');
      onComplete();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to complete reconciliation.', 'danger');
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const beginBal = Number(recon.beginning_balance) || 0;
  const clearedDep = Number(recon.cleared_deposits) || 0;
  const clearedWith = Number(recon.cleared_withdrawals) || 0;
  const clearedBalance = beginBal + clearedDep - clearedWith;
  const difference = Number(recon.difference) || 0;
  const isBalanced = Math.abs(difference) < 0.01;

  // Split transactions into deposits (money in) and withdrawals (money out)
  const deposits = transactions.filter(t => t.amount < 0);
  const withdrawals = transactions.filter(t => t.amount >= 0);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button variant="plain" color="neutral" startDecorator={<ArrowBack />} onClick={onBack}>
            Back
          </Button>
          <div>
            <Typography level="h2" sx={{ fontSize: { xs: '1.25rem', md: undefined }, wordBreak: 'break-word' }}>Reconcile: {recon.account_name}</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Statement date: {formatDate(recon.statement_date)}
            </Typography>
          </div>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isBalanced ? (
            <Button color="success" startDecorator={<CheckCircle />} onClick={() => handleFinish(false)}>
              Finish Now
            </Button>
          ) : (
            <Button color="warning" variant="outlined" onClick={() => setConfirmFinishOpen(true)}>
              Finish with Adjustment
            </Button>
          )}
        </Box>
      </Box>

      {/* Summary Bar */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center', minWidth: 120 }}>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Beginning Balance</Typography>
              <Typography level="title-md">{formatCurrency(recon.beginning_balance)}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 120 }}>
              <Typography level="body-xs" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <CheckCircle size={14} /> Cleared Deposits
              </Typography>
              <Typography level="title-md" color="success">
                +{formatCurrency(recon.cleared_deposits)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 120 }}>
              <Typography level="body-xs" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <CheckCircle size={14} /> Cleared Payments
              </Typography>
              <Typography level="title-md" color="danger">
                -{formatCurrency(recon.cleared_withdrawals)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 120 }}>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Cleared Balance</Typography>
              <Typography level="title-md">{formatCurrency(clearedBalance)}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 120 }}>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Statement Balance</Typography>
              <Typography level="title-md">{formatCurrency(recon.statement_balance)}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 140 }}>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Difference</Typography>
              <Typography
                level="title-lg"
                fontWeight="xl"
                color={isBalanced ? 'success' : 'danger'}
              >
                {formatCurrency(difference)}
              </Typography>
              {isBalanced && (
                <Chip size="sm" color="success" variant="soft" sx={{ mt: 0.5 }}>
                  Balanced
                </Chip>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
          {/* Deposits (Money In) */}
          <Box sx={{ flex: 1, p: 1 }}>
            <Typography level="title-md" sx={{ mb: 1 }}>
              Deposits & Credits ({deposits.length})
            </Typography>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: '60vh' }}>
              <Table size="sm" stickyHeader sx={{ '& td, & th': { py: 1.5, px: 2 } }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>Date</th>
                    <th>Description</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.length === 0 ? (
                    <tr><td colSpan={4}><Typography level="body-sm" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>No deposits found</Typography></td></tr>
                  ) : deposits.map(txn => (
                    <tr
                      key={txn.id}
                      style={{ opacity: clearing === txn.id ? 0.5 : 1, cursor: 'pointer' }}
                      onClick={() => handleToggleClear(txn)}
                    >
                      <td>
                        <Checkbox
                          checked={!!txn.is_cleared}
                          disabled={clearing === txn.id}
                          onChange={() => handleToggleClear(txn)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td>
                        <Typography level="body-xs">{formatDate(txn.transaction_date)}</Typography>
                      </td>
                      <td>
                        <Typography level="body-sm" sx={{ textDecoration: txn.is_cleared ? 'line-through' : 'none', opacity: txn.is_cleared ? 0.7 : 1 }}>
                          {txn.name}
                        </Typography>
                        {txn.merchant_name && (
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{txn.merchant_name}</Typography>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Typography level="body-sm" color="success" fontWeight={txn.is_cleared ? 'lg' : 'md'}>
                          {formatCurrency(Math.abs(txn.amount))}
                        </Typography>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          </Box>

          {/* Withdrawals (Money Out) */}
          <Box sx={{ flex: 1, p: 1 }}>
            <Typography level="title-md" sx={{ mb: 1 }}>
              Payments & Withdrawals ({withdrawals.length})
            </Typography>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: '60vh' }}>
              <Table size="sm" stickyHeader sx={{ '& td, & th': { py: 1.5, px: 2 } }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>Date</th>
                    <th>Description</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.length === 0 ? (
                    <tr><td colSpan={4}><Typography level="body-sm" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>No payments found</Typography></td></tr>
                  ) : withdrawals.map(txn => (
                    <tr
                      key={txn.id}
                      style={{ opacity: clearing === txn.id ? 0.5 : 1, cursor: 'pointer' }}
                      onClick={() => handleToggleClear(txn)}
                    >
                      <td>
                        <Checkbox
                          checked={!!txn.is_cleared}
                          disabled={clearing === txn.id}
                          onChange={() => handleToggleClear(txn)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td>
                        <Typography level="body-xs">{formatDate(txn.transaction_date)}</Typography>
                      </td>
                      <td>
                        <Typography level="body-sm" sx={{ textDecoration: txn.is_cleared ? 'line-through' : 'none', opacity: txn.is_cleared ? 0.7 : 1 }}>
                          {txn.name}
                        </Typography>
                        {txn.merchant_name && (
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{txn.merchant_name}</Typography>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Typography level="body-sm" color="danger" fontWeight={txn.is_cleared ? 'lg' : 'md'}>
                          {formatCurrency(Math.abs(txn.amount))}
                        </Typography>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          </Box>
        </Box>
      )}

      {/* Confirm Finish with Adjustment Modal */}
      <Modal open={confirmFinishOpen} onClose={() => setConfirmFinishOpen(false)}>
        <ModalDialog>
          <DialogTitle>
            <Warning color="var(--joy-palette-warning-500)" />
            Finish with Adjustment?
          </DialogTitle>
          <DialogContent>
            <Typography level="body-md" sx={{ mb: 2 }}>
              The reconciliation has a difference of <strong>{formatCurrency(difference)}</strong>.
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              An adjusting journal entry will be created to the "Reconciliation Discrepancies" account
              to balance the reconciliation. This is common for small rounding differences or bank fees
              not yet recorded.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="warning" onClick={() => handleFinish(true)}>
              Create Adjustment & Finish
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setConfirmFinishOpen(false)}>
              Keep Reconciling
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        color={snackbar.color}
        variant="soft"
        startDecorator={snackbar.color === 'success' ? <CheckCircle /> : <Warning />}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
};

// ─── Main Reconciliation Page ──────────────────────────────────────────
export const BankReconciliation: React.FC = () => {
  const [reconciliations, setReconciliations] = useState<BankReconciliationType[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [activeRecon, setActiveRecon] = useState<BankReconciliationType | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reconToDelete, setReconToDelete] = useState<BankReconciliationType | null>(null);
  const [undoModalOpen, setUndoModalOpen] = useState(false);
  const [reconToUndo, setReconToUndo] = useState<BankReconciliationType | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean; message: string; color: 'success' | 'danger' | 'warning';
  }>({ open: false, message: '', color: 'success' });

  const [newRecon, setNewRecon] = useState({
    account: '' as string | number,
    statement_date: '',
    statement_balance: '',
  });

  const showSnackbar = (message: string, color: 'success' | 'danger' | 'warning') => {
    setSnackbar({ open: true, message, color });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [reconcRes, accountsRes] = await Promise.all([
        apiAccountingClient.getBankReconciliations(),
        apiAccountingClient.getBankAccounts(),
      ]);
      setReconciliations(reconcRes.results);
      setAccounts(accountsRes.results);
    } catch (err) {
      console.error('Failed to fetch reconciliations:', err);
      setError('Failed to load reconciliation data.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newRecon.account || !newRecon.statement_date || !newRecon.statement_balance) {
      showSnackbar('Please fill in all fields.', 'warning');
      return;
    }
    try {
      const created = await apiAccountingClient.createBankReconciliation({
        account: Number(newRecon.account),
        statement_date: newRecon.statement_date,
        statement_balance: Number(newRecon.statement_balance),
      } as Omit<BankReconciliationType, 'id' | 'created_at' | 'updated_at'>);
      setCreateModalOpen(false);
      setNewRecon({ account: '', statement_date: '', statement_balance: '' });
      await fetchData();
      // Immediately open the working screen
      setActiveRecon(created);
      showSnackbar('Reconciliation created. Start checking off transactions.', 'success');
    } catch (err) {
      console.error('Failed to create reconciliation:', err);
      showSnackbar('Failed to create reconciliation.', 'danger');
    }
  };

  const handleDelete = async () => {
    if (!reconToDelete) return;
    try {
      await apiAccountingClient.deleteReconciliation(reconToDelete.id);
      setDeleteModalOpen(false);
      setReconToDelete(null);
      await fetchData();
      showSnackbar('Reconciliation deleted.', 'success');
    } catch (err) {
      console.error('Failed to delete reconciliation:', err);
      showSnackbar('Failed to delete reconciliation.', 'danger');
    }
  };

  const handleUndo = async () => {
    if (!reconToUndo) return;
    try {
      await apiAccountingClient.undoReconciliation(reconToUndo.id);
      setUndoModalOpen(false);
      setReconToUndo(null);
      await fetchData();
      showSnackbar('Reconciliation undone. You can continue reconciling.', 'success');
    } catch (err) {
      console.error('Failed to undo reconciliation:', err);
      showSnackbar('Failed to undo reconciliation.', 'danger');
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const getStatusColor = (s: string): 'success' | 'danger' | 'warning' | 'neutral' => {
    switch (s) {
      case 'COMPLETED': return 'success';
      case 'DISCREPANCY': return 'danger';
      case 'IN_PROGRESS': return 'warning';
      default: return 'neutral';
    }
  };

  // If actively reconciling, show the working screen
  if (activeRecon) {
    return (
      <ReconcileWorking
        reconciliation={activeRecon}
        onBack={() => { setActiveRecon(null); fetchData(); }}
        onComplete={() => { setActiveRecon(null); fetchData(); }}
      />
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
        <div>
          <Typography level="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', md: undefined } }}>Bank Reconciliation</Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Match your bank statements against book records
          </Typography>
        </div>
        <Button
          startDecorator={<Add />}
          onClick={() => setCreateModalOpen(true)}
          disabled={accounts.length === 0}
        >
          New Reconciliation
        </Button>
      </Box>

      {accounts.length === 0 && (
        <Alert color="warning" sx={{ mb: 3 }}>
          Connect a bank account first to begin reconciliation.
        </Alert>
      )}

      {reconciliations.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CompareArrows size={48} color="var(--joy-palette-text-tertiary)" style={{ marginBottom: 16 }} />
            <Typography level="h4" sx={{ mb: 1 }}>No reconciliations yet</Typography>
            <Typography level="body-md" sx={{ color: 'text.secondary' }}>
              Start a reconciliation to match your bank statement with your books
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
          <Table stickyHeader sx={{ minWidth: 880 }}>
            <thead>
              <tr>
                <th>Account</th>
                <th>Statement Date</th>
                <th>Statement Balance</th>
                <th>Beginning Balance</th>
                <th>Difference</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reconciliations.map((recon) => {
                const account = accounts.find(a => a.id === recon.account);
                return (
                  <tr key={recon.id}>
                    <td>
                      <Typography level="body-sm" fontWeight="md">
                        {account?.account_name || recon.account_name || `Account #${recon.account}`}
                      </Typography>
                    </td>
                    <td>{formatDate(recon.statement_date)}</td>
                    <td>{formatCurrency(recon.statement_balance)}</td>
                    <td>{formatCurrency(recon.beginning_balance)}</td>
                    <td>
                      <Typography
                        level="body-sm"
                        color={Math.abs(recon.difference) < 0.01 ? 'success' : 'danger'}
                        fontWeight="lg"
                      >
                        {formatCurrency(recon.difference)}
                      </Typography>
                    </td>
                    <td>
                      <Chip size="sm" color={getStatusColor(recon.status)}>
                        {recon.status.replace('_', ' ')}
                      </Chip>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {recon.status === 'IN_PROGRESS' && (
                          <>
                            <Button size="sm" onClick={() => setActiveRecon(recon)}>
                              Continue
                            </Button>
                            <IconButton
                              size="sm"
                              variant="outlined"
                              color="danger"
                              onClick={() => { setReconToDelete(recon); setDeleteModalOpen(true); }}
                            >
                              <Delete />
                            </IconButton>
                          </>
                        )}
                        {recon.status === 'COMPLETED' && (
                          <>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="warning"
                              startDecorator={<Undo />}
                              onClick={() => { setReconToUndo(recon); setUndoModalOpen(true); }}
                            >
                              Undo
                            </Button>
                            <IconButton
                              size="sm"
                              variant="outlined"
                              color="danger"
                              onClick={() => { setReconToDelete(recon); setDeleteModalOpen(true); }}
                            >
                              <Delete />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Sheet>
      )}

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <Warning />
            Delete Reconciliation
          </DialogTitle>
          <DialogContent>
            Are you sure you want to delete this reconciliation for {reconToDelete?.account_name}
            (statement date: {reconToDelete ? formatDate(reconToDelete.statement_date) : ''})?
            {reconToDelete?.status === 'COMPLETED' && (
              <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
                This will also revert all cleared transactions back to their previous status.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="solid" color="danger" onClick={handleDelete}>
              Delete
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Undo Confirmation Modal */}
      <Modal open={undoModalOpen} onClose={() => setUndoModalOpen(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <Undo />
            Undo Reconciliation
          </DialogTitle>
          <DialogContent>
            This will revert the reconciliation for {reconToUndo?.account_name} back to "In Progress"
            and un-clear all transactions. You can then continue reconciling.
          </DialogContent>
          <DialogActions>
            <Button variant="solid" color="warning" onClick={handleUndo}>
              Undo Reconciliation
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setUndoModalOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Create Modal */}
      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <ModalDialog>
          <DialogTitle>New Bank Reconciliation</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography level="body-sm" sx={{ mb: 0.5 }}>Bank Account</Typography>
                <Select
                  placeholder="Select account..."
                  value={newRecon.account ? String(newRecon.account) : null}
                  onChange={(_e, val) => setNewRecon(prev => ({ ...prev, account: val ? Number(val) : '' }))}
                >
                  {accounts.map(acc => (
                    <Option key={acc.id} value={String(acc.id)}>
                      {acc.account_name} ({acc.account_type})
                    </Option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Typography level="body-sm" sx={{ mb: 0.5 }}>Statement Ending Date</Typography>
                <Input
                  type="date"
                  value={newRecon.statement_date}
                  onChange={e => setNewRecon(prev => ({ ...prev, statement_date: e.target.value }))}
                />
              </Box>
              <Box>
                <Typography level="body-sm" sx={{ mb: 0.5 }}>Statement Ending Balance</Typography>
                <Input
                  type="number"
                  startDecorator="$"
                  placeholder="0.00"
                  value={newRecon.statement_balance}
                  onChange={e => setNewRecon(prev => ({ ...prev, statement_balance: e.target.value }))}
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCreate}>Start Reconciling</Button>
            <Button variant="plain" color="neutral" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        color={snackbar.color}
        variant="soft"
        startDecorator={snackbar.color === 'success' ? <CheckCircle /> : <Warning />}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
};
